import { Heart, Leaf, Sun, Moon } from 'lucide-react';

export default function Wellness() {
  return (
    <div className="min-h-screen py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-[#2D3436] mb-4" data-testid="wellness-page-title">
            Ayurveda & <span className="text-primary">Wellness</span>
          </h1>
          <p className="text-lg text-[#636E72] max-w-3xl mx-auto">
            Discover the ancient science of life and holistic healing
          </p>
        </div>

        <div className="bg-white p-12 rounded-2xl shadow-sm mb-12">
          <h2 className="text-3xl font-heading font-semibold text-[#2D3436] mb-6">What is Ayurveda?</h2>
          <div className="space-y-4 text-[#636E72] leading-relaxed">
            <p>
              Ayurveda is an ancient Indian medical science that has been practiced for over 5,000 years. The word "Ayurveda" comes from Sanskrit: "Ayur" meaning life, and "Veda" meaning knowledge or science. It represents a comprehensive system of medicine focused on maintaining health, preventing disease, and treating illness through natural, holistic approaches.
            </p>
            <p>
              Unlike conventional medicine that often treats symptoms, Ayurveda addresses the root cause of health issues by considering the whole person - body, mind, and spirit. This holistic approach recognizes that each individual is unique and requires personalized treatment.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-xl">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
              <Heart className="text-primary" size={32} />
            </div>
            <h3 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">Root-Cause Treatment</h3>
            <p className="text-[#636E72] leading-relaxed">
              Ayurveda focuses on identifying and treating the underlying cause of disease rather than just managing symptoms. This approach leads to lasting healing and prevents recurrence.
            </p>
          </div>

          <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 p-8 rounded-xl">
            <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6">
              <Leaf className="text-secondary" size={32} />
            </div>
            <h3 className="text-2xl font-heading font-semibold text-[#2D3436] mb-4">Natural & Safe</h3>
            <p className="text-[#636E72] leading-relaxed">
              All treatments use natural herbs, minerals, and lifestyle modifications. This minimizes side effects and works in harmony with the body's natural healing processes.
            </p>
          </div>
        </div>

        <div className="bg-white p-12 rounded-2xl shadow-sm mb-12">
          <h2 className="text-3xl font-heading font-semibold text-[#2D3436] mb-8 text-center">Pillars of Ayurvedic Treatment</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="text-primary" size={36} />
              </div>
              <h4 className="text-xl font-heading font-semibold text-[#2D3436] mb-3">Diet (Ahara)</h4>
              <p className="text-[#636E72] leading-relaxed">
                Personalized nutrition plans based on your constitution and health needs. Food as medicine for healing and prevention.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sun className="text-primary" size={36} />
              </div>
              <h4 className="text-xl font-heading font-semibold text-[#2D3436] mb-3">Lifestyle (Vihara)</h4>
              <p className="text-[#636E72] leading-relaxed">
                Daily routines, exercise, sleep patterns, and stress management tailored to promote optimal health and balance.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Moon className="text-primary" size={36} />
              </div>
              <h4 className="text-xl font-heading font-semibold text-[#2D3436] mb-3">Medicine (Aushadha)</h4>
              <p className="text-[#636E72] leading-relaxed">
                Herbal formulations and therapies specifically chosen to address your unique health concerns and restore balance.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl mb-12">
          <h2 className="text-3xl font-heading font-semibold mb-8 text-center">Highly Effective for Lifestyle Disorders</h2>
          
          <p className="text-lg leading-relaxed mb-8 text-center max-w-3xl mx-auto opacity-90">
            Modern sedentary lifestyles have led to a surge in chronic conditions. Ayurveda's holistic approach is particularly effective for:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Metabolic Disorders</h4>
              <p className="text-sm opacity-90">Obesity, diabetes, thyroid issues, metabolic syndrome</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Cardiovascular Health</h4>
              <p className="text-sm opacity-90">Hypertension, high cholesterol, heart disease prevention</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Digestive Disorders</h4>
              <p className="text-sm opacity-90">IBS, GERD, hyperacidity, chronic indigestion</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Mental Health</h4>
              <p className="text-sm opacity-90">Stress, anxiety, insomnia, depression</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Joint & Spinal Issues</h4>
              <p className="text-sm opacity-90">Arthritis, back pain, cervical spondylosis</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
              <h4 className="font-semibold mb-2">Skin Conditions</h4>
              <p className="text-sm opacity-90">Eczema, psoriasis, acne, chronic skin issues</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-12 rounded-2xl shadow-sm">
          <h2 className="text-3xl font-heading font-semibold text-[#2D3436] mb-6 text-center">The Ayurvedic Philosophy</h2>
          <div className="space-y-4 text-[#636E72] leading-relaxed max-w-3xl mx-auto">
            <p>
              <strong className="text-[#2D3436]">Prevention is Better Than Cure:</strong> Ayurveda emphasizes maintaining health through proper diet, lifestyle, and daily routines rather than waiting for disease to occur.
            </p>
            <p>
              <strong className="text-[#2D3436]">Personalized Care:</strong> Every individual has a unique constitution (Prakriti). Treatments are customized based on this individual nature, ensuring maximum effectiveness.
            </p>
            <p>
              <strong className="text-[#2D3436]">Mind-Body Connection:</strong> Mental and emotional health are given equal importance alongside physical health. True wellness comes from balance in all aspects of life.
            </p>
            <p>
              <strong className="text-[#2D3436]">Long-term Health:</strong> Rather than quick fixes, Ayurveda focuses on sustainable lifestyle changes that promote lifelong health and vitality.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}